import { Injectable } from '@angular/core';
import * as moment from 'moment';

import { DayEntryModel, UserModel } from '../../models/models';
import { ApiProvider } from '../api/api';
import { ConfigurationProvider } from '../configuration/configuration';

@Injectable()
export class DayEntryProvider {
  private items: DayEntryModel[] = [];
  private date: moment.Moment;

  constructor(
    private api: ApiProvider,
    private config: ConfigurationProvider) {
  }

  async DeleteFoodItems(ids: number[]) {
    var promises = [];
    for (let id of ids) {
      promises.push(this.DeleteFoodItem(id));
    }
    await Promise.all(promises);
  }

  DeleteFoodItem(id: number) {
    return new Promise((resolve, reject) => {
      this.api.delete(`day-entry/food/${id}`).subscribe((res) => {
        const data = res.json();
        if (data.success) {
          resolve(true);
        }
        resolve(false);
      });
    });
  }

  async DeleteSupplementItems(ids: number[]) {
    var promises = [];
    for (let id of ids) {
      promises.push(this.DeleteSupplementItem(id));
    }
    await Promise.all(promises);
  }

  DeleteSupplementItem(id: number) {
    return new Promise((resolve, reject) => {
      this.api.delete(`day-entry/supplement/${id}`).subscribe((res) => {
        const data = res.json();
        if (data.success) {
          resolve(true);
        }
        resolve(false);
      });
    });
  }

  Get(date) {
    return this.items.find(x => {
      const d1 = moment(x.Date).format('YYYY-MM-DD');
      const d2 = date.format('YYYY-MM-DD');
      return d1 === d2;
    });
  }

  GetDate() {
    return this.date;
  }

  List() {
    return this.items;
  }

  Load(user_id, date: moment.Moment = moment(), force: boolean = false, merge: boolean = false) {
    return new Promise((resolve, reject) => {
      this.date = date;

      if (this.items.length === 0 || force) {
        this.LoadFromApi(user_id, date.format('YYYY-MM-DD')).then((data: DayEntryModel[]) => {
          data.map(dayEntry => {
            dayEntry.NutritionTarget = this.config.getNutritionTarget(dayEntry.BodyComposition.Weight, dayEntry.BodyComposition.Height, dayEntry.BodyComposition.Age),
            dayEntry.TargetPercentage = this.config.getNutritionTargetPercent(dayEntry.Nutrition, dayEntry.NutritionTarget);
            dayEntry.MacroPercentage = this.config.getMacroTargetPercent(dayEntry.Nutrition);
          });

          if (merge) {
            data.map(x => {
              this.items = this.items.map(y => {
                if (y.Date === x.Date) {
                  y = x;
                }
                return y;
              });
            });
          }
          else {
            if (this.items.length === 0) {
              this.items = this.items.concat(data);
            }
            else {
              data.map(x => {
                const hasDate = this.items.find(y => moment(x.Date).format('YYYY-MM-DD') === moment(y.Date).format('YYYY-MM-DD'));
                if (hasDate === undefined) {
                  this.items.push(x);
                }
              });
            }
          }

          resolve(this.items);
        });
      }
      else {
        resolve(this.items);
      }
    });
  }

  LoadFoodItems(user_id, dayEntry: DayEntryModel, all: boolean = false) {
    return new Promise((resolve, reject) => {
      let date = moment(dayEntry.Date).format('YYYY-MM-DD');
      this.api.get(`day-entry/${user_id}/${date}/food` + (all ? '/all' : '')).subscribe((data) => {
        const arr = data.json();

        let obj: any[] = [];

        for(var j = 0; j < arr.length; j++) {
          var item = arr[j];

          const time = moment(item.time, 'H:mm:ss');

          obj.push({
            DayEntryFoodId: item.id,
            DayEntryId: dayEntry._id,
            IsMeal: item.is_meal,
            MealId: item.meal_id,
            FoodId: item.food_id,
            Time: time.format('HH:mm'),
            TimeDisplay: time.format('hh:mm a'),
            MealName: item.meal_name,
            Name: item.name,
            Brand: item.brand,
            Portion: item.portion,
            Serving: Number(item.serving),
            ServingSize: item.serving_size,
            Nutrition: {
              Calories: Number(item.calories),
              Fat: Number(item.fat),
              Carbohydrates: Number(item.carbohydrates),
              Protein: Number(item.protein)
            }
          });
        }

        // sort food by time
        obj.sort((a: any, b: any) => {
          if (moment(a.Time, 'HH:mm') > moment(b.Time, 'HH:mm')) return 1;
          else if (moment(a.Time, 'HH:mm') < moment(b.Time, 'HH:mm')) return -1;
          else return 0;
        });

        dayEntry.FoodItems = obj;

        this.items.map(x => {
          if (x.Date === dayEntry.Date) x = dayEntry;
        });

        resolve(dayEntry);
      });
    });
  }

  LoadFoodSearch() {
    return new Promise((resolve, reject) => {
      this.api.get(`day-entry/food-search`).subscribe((data) => {
        const arr = data.json();
        let obj: any[] = [];

        for (let item of arr) {
          obj.push({
            _id: item.id,
            IsMeal: item.is_meal,
            MealId: item.meal_id,
            FoodId: item.food_id,
            Name: item.name,
            FoodCount: item.food_count,
            Brand: item.brand !== '' ? item.brand : 'Meal: ' + item.food_count + ' item(s)',
            Portion: item.portion,
            Serving: 1,
            ServingSize: item.serving_size,
            Nutrition: {
              Calories: Number(item.calories),
              Fat: Number(item.fat),
              Carbohydrates: Number(item.carbohydrates),
              Protein: Number(item.protein)
            }
          });
        }

        resolve(obj);
      });
    });
  }

  LoadSupplements(user_id, dayEntry: DayEntryModel) {
    return new Promise((resolve, reject) => {
      let date = moment(dayEntry.Date).format('YYYY-MM-DD');
      this.api.get(`day-entry/${user_id}/${date}/supplement`).subscribe((data) => {
        const arr = data.json();

        let obj: any[] = [];

        for(var j = 0; j < arr.length; j++) {
          var item = arr[j];

          const time = moment(item.time, 'H:mm:ss');

          obj.push({
            DayEntrySupplementId: item.id,
            DayEntryId: dayEntry._id,
            IsStack: item.is_stack,
            SupplementId: item.supplement_id,
            StackId: item.supplement_stack_id,
            Time: time.format('HH:mm'),
            TimeDisplay: time.format('hh:mm a'),
            Name: item.name,
            Brand: item.brand,
            Portion: item.portion,
            Serving: Number(item.serving),
            StartDate: item.start_date,
            TotalServings: item.total_servings
          });
        }

        // sort by time
        obj.sort((a: any, b: any) => {
          if (moment(a.Time, 'HH:mm') > moment(b.Time, 'HH:mm')) return 1;
          else if (moment(a.Time, 'HH:mm') < moment(b.Time, 'HH:mm')) return -1;
          else return 0;
        });

        dayEntry.Supplements = obj;

        this.items.map(x => {
          if (x.Date === dayEntry.Date) x = dayEntry;
        });

        resolve(dayEntry);
      });
    });
  }

  LoadSupplementSearch() {
    return new Promise((resolve, reject) => {
      this.api.get(`day-entry/supplement-search`).subscribe((data) => {
        const arr = data.json();
        let obj: any[] = [];

        for (let item of arr) {
          obj.push({
            IsStack: item.is_stack,
            StackId: item.stack_id,
            SupplementId: item.supplement_id,
            Name: item.name,
            SupplementCount: item.supplement_count,
            Brand: item.brand !== '' ? item.brand : 'Stack: ' + item.supplement_count + ' item(s)',
            Portion: item.portion,
            StartDate: item.start_date,
            Serving: 1
          });
        }

        resolve(obj);
      });
    });
  }

  Save(user: UserModel, item: DayEntryModel, saveFood: boolean = true) {
    return new Promise((resolve, reject) => {

      // declare new day entry
      let newItem: any = {
        _id: item._id,
        UserId: user._id,
        BodyComposition: {
          Weight: item.BodyComposition.Weight
        },
        Date: moment(item.Date).format('YYYY-MM-DD'),
        Hydration: item.Hydration,
        Notes: item.Notes
      };

      // insert or update day entry
      this.api.post('day-entry/', newItem).subscribe((res) => {
        const data = res.json();
        if (data.success) {
          newItem._id = data.id;
          newItem.FoodItems = [];

          if (saveFood) {

            // each food item in meal
            item.FoodItems.map((elem: any, j) => {

              // add food items
              newItem.FoodItems.push({
                DayEntryFoodId: elem.DayEntryFoodId || 0,
                DayEntryId: newItem._id,
                Time: moment(elem.Time, 'hh:mm A').format('HH:mm'),
                MealId: elem.MealId,
                FoodId: elem.FoodId,
                Serving: elem.Serving,
              } as any);
            });

            this.api.post('day-entry/food', newItem.FoodItems).subscribe((res) => {
              const data = res.json();
              if (data.success) {
                resolve(newItem);
              }
            });
          }
          else {
            resolve(newItem);
          }
        }
      });
    });
  }

  SaveSupplements(user: UserModel, item: DayEntryModel) {
    return new Promise((resolve, reject) => {

      // declare new day entry
      let newItem: any = {
        _id: item._id,
        UserId: user._id,
        BodyComposition: {
          Weight: item.BodyComposition.Weight
        },
        Date: moment(item.Date).format('YYYY-MM-DD'),
        Hydration: item.Hydration,
        Notes: item.Notes
      };

      // insert or update day entry
      this.api.post('day-entry/', newItem).subscribe((res) => {
        const data = res.json();
        if (data.success) {
          newItem._id = data.id;
          newItem.Supplements = [];

          // each item
          item.Supplements.map((elem: any, j) => {

            // add items
            newItem.Supplements.push({
              DayEntrySupplementId: elem.DayEntrySupplementId || 0,
              DayEntryId: newItem._id,
              StartDate: moment(elem.StartDate, 'YYYY-MM-DD').format('YYYY-MM-DD'),
              Time: moment(elem.Time, 'hh:mm A').format('HH:mm'),
              SupplementId: elem.SupplementId,
              StackId: elem.StackId,
              Serving: elem.Serving,
            } as any);
          });

          this.api.post('day-entry/supplements', newItem.Supplements).subscribe((res) => {
            const data = res.json();
            if (data.success) {
              resolve(newItem);
            }
          });
        }
      });
    });
  }

  SetDate(date: moment.Moment) {
    this.date = date;
  }

  SyncFitbit(day_entry) {
    return new Promise((resolve, reject) => {
      this.api.get(`fitbit/data/sync/${day_entry.UserId}/${moment(day_entry.Date).format('YYYY-MM-DD')}`).subscribe((data) => {
        const summary = data.json();
        day_entry.FitbitActivityCalories = summary.activity_calories;
        day_entry.FitbitCalories = summary.calories;
        day_entry.FitbitDistance = this.config.fixDecimals(summary.distance);
        day_entry.FitbitSteps = summary.steps;
        day_entry.Activities = this.BuildActivityData(day_entry.user_id, summary.activities);
        resolve(day_entry);
      });
    });
  }

  private BuildActivityData(user_id, activities) {

    // sort
    activities.sort((a, b) => {
      if (a.start_time < b.start_time) return 1;
      else if (a.start_time > b.start_time) return -1;
      else return 0;
    });

    // build activities
    return activities.map(item => {
      item.active_duration = item.active_duration / 1000 / 60;
      const h = this.config.pad(Math.floor(item.active_duration / 60));
      const m = this.config.pad(Math.floor((item.active_duration / 60) % 1 * 60));
      const s = this.config.pad(Math.floor(item.active_duration % 1 * 60));
      const startTime = item.start_time.indexOf('GMT') > 0 ? 
        moment(item.start_time).utc() : moment(item.start_time);
      return {
        _id: item.day_entry_activity_id,
        ActiveDuration: item.active_duration,
        Calories: item.calories,
        Distance: item.distance,
        Duration: item.duration,
        DurationDisplay: `${h}:${m}:${s}`,
        Name: item.name,
        StartTime: startTime.format('HH:mm'),
        StartTimeDisplay: startTime.format('hh:mm a'),
        Steps: item.steps,
        ActivityCount: item.activity_count,
        UserId: user_id
      } 
    });
  }

  private BuildSupplementData(user_id, supplements) {

    // build supplements
    let suppData = supplements.map(item => {
      const time = moment(item.time, 'H:mm:ss');
      return {
        _id: item.day_entry_supplement_id,
        StartDate: item.start_date,
        Name: item.name,
        Brand: item.brand,
        Time: time.format('HH:mm'),
        TimeDisplay: time.format('hh:mm a'),
        UserId: user_id,
        Serving: item.serving,
        TotalServings: item.total_servings
      } 
    });

    // sort
    suppData.sort((a: any, b: any) => {
      if (moment(a.Time, 'HH:mm') > moment(b.Time, 'HH:mm')) return 1;
      else if (moment(a.Time, 'HH:mm') < moment(b.Time, 'HH:mm')) return -1;
      else return 0;
    });

    return suppData;
  }

  private LoadFromApi(user_id, date) {
    return new Promise((resolve, reject) => {
      this.api.get(`day-entry/${user_id}/${date}`).subscribe((data) => {
        const arr = data.json();

        let obj: DayEntryModel[] = [];

        for(var j = 0; j < arr.length; j++) {
          var item = arr[j];

          // item.date = item.date.substring(0, item.date.lastIndexOf(' '));

          obj.push({
            _id: item.id,
            BodyComposition: {
              Weight: item.body_weight,
              Height: item.body_height,
              Age: item.age
            },
            Date: item.date,
            Hydration: item.hydration,
            Notes: item.notes,
            Nutrition: {
              Calories: Number(item.calories),
              Fat: Number(item.fat),
              Carbohydrates: Number(item.carbohydrates),
              Protein: Number(item.protein)
            },
            TargetPercentage: {
              Calories: 0,
              Fat: 0,
              Carbohydrates: 0,
              Protein: 0
            },
            // FitbitActivityCalories: Number(item.fitbit_activity_calories),
            // FitbitCalories: Number(item.fitbit_calories),
            // FitbitDistance: Number(item.fitbit_distance),
            // FitbitSteps: Number(item.fitbit_steps),
            UserId: user_id,
            // Activities: this.BuildActivityData(user_id, item.activity),
            Supplements: this.BuildSupplementData(user_id, item.supplements)
          } as DayEntryModel);
        }

        resolve(obj);
      });
    });
  }

}



// WEBPACK FOOTER //
// ./src/providers/day-entry/day-entry.ts